import { faBan, faUserPlus } from "@fortawesome/pro-solid-svg-icons";
import {
    ApiBaseService,
    type PaginatedFetch,
    type PaginatedFetchOptions,
} from "./ApiBaseService";
import type { Person } from "~/models/Person";
import type { User } from "~/models/User";

export default class PeopleService extends ApiBaseService {
    public list(
        options?: Partial<PaginatedFetchOptions>,
    ): PaginatedFetch<Person[]> {
        return this.paginate<Person[]>(
            this.route("api.people.index", "/people"),
            options as PaginatedFetchOptions,
        );
    }

    public find(id: number): Promise<ApiResponse<Person>> {
        return this.request<Person>(
            this.route("api.people.show", "/people/{person}", {
                person: id,
            }),
            {
                method: "GET",
            },
        );
    }

    public update(
        id: number,
        data: Partial<Person>,
    ): Promise<ApiResponse<Person>> {
        return this.request(
            this.route("api.people.store", "/people/{person}", {
                person: id,
            }),
            {
                method: "PATCH",
                body: data,
            },
        );
    }

    public delete(id: number): Promise<null> {
        return this.request(
            this.route("api.people.destroy", "/people/{person}", {
                person: id,
            }),
            {
                method: "DELETE",
            },
        );
    }

    public dropdown(user: User) {
        const api = useApi();
        let label = "Invite";
        let notAcceptedInvite = false;
        let resetPassword = false;
        if (user.is_invited) {
            if (!user.accepted_invite) {
                label = "Re-send invite";
                notAcceptedInvite = true;
            } else {
                label = "Reset password";
                resetPassword = true;
            }
        }
        const lists: Array<Array<Record<string, any>>> = [
            [
                {
                    label,
                    icon: faUserPlus,
                    click: async () => {
                        try {
                            if (resetPassword) {
                                await api.auth.forgotPassword(user.email);

                                useToast().add({
                                    title: `The password reset email has been sent to ${user.email}.`,
                                });
                            } else {
                                await api.users.invite({
                                    email: user.email as string,
                                });
                                if (notAcceptedInvite) {
                                    useToast().add({
                                        title: `The invitation has been re-sent to ${user.email}.`,
                                    });
                                } else {
                                    useToast().add({
                                        title: `The invitation has been sent to ${user.email}.`,
                                    });
                                }
                            }
                            refreshNuxtData();
                        } catch (e: any) {
                            if (e?.errors?.email) {
                                useToast().add({
                                    title: e.errors.email.join(", "),
                                    color: "red",
                                });
                            }
                        }
                    },
                },
            ],
        ];

        if (notAcceptedInvite) {
            lists.push([
                {
                    label: "Revoke invite",
                    class: "text-red-500",
                    icon: faBan,
                    click: async () => {
                        try {
                            useToast().add({
                                title: "Are you sure?",
                                description: `You're about to revoke the invitation for ${user.email ?? ""}.`,
                                icon: "i-heroicons-question-mark-circle-16-solid",
                                timeout: 10000,
                                actions: [
                                    {
                                        label: "Yes, revoke",
                                        color: "primary",
                                        click: async () => {
                                            await api.users.revokeInvite(
                                                user.id,
                                            );

                                            useToast().add({
                                                title: `${user.email} invite has been revoked.`,
                                            });

                                            refreshNuxtData();
                                        },
                                    },
                                    {
                                        label: "No, cancel",
                                    },
                                ],
                            });
                        } catch (e: any) {
                            if (e?.errors?.email) {
                                useToast().add({
                                    title: e.errors.email.join(", "),
                                    color: "red",
                                });
                            }
                        }
                    },
                },
            ]);
        }

        return lists;
    }
}
